import { useMemo } from "react";
import { isBankAccount } from "../constants/accountTypes";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import {
  getSubscriptionLimitsEnabled,
  isFeatureAllowed,
  useEnhancedSubscriptionFeatures,
} from "./useEnhancedSubscriptionFeatures";
import { useFinancialQueries } from "./useFinancialQueries";

const ENABLE_SUBSCRIPTION_LIMITS = getSubscriptionLimitsEnabled();

interface BankAccountWithType {
  accountType?: string;
  [key: string]: unknown;
}

interface UseBankAccountLimitResult {
  /** Whether user can add a new bank account */
  canAddBankAccount: boolean;
  /** Current number of bank accounts (excluding cash) */
  currentBankAccountCount: number;
  /** Maximum allowed bank accounts from subscription */
  maxBankAccounts: number | null;
  /** Whether subscription data is loading */
  isLoading: boolean;
  /** Subscription data */
  subscriptionData: ReturnType<typeof useEnhancedSubscriptionFeatures>["data"];
}

/**
 * Custom hook to check bank account limits
 *
 * Encapsulates the logic for:
 * - Fetching subscription limits
 * - Counting existing bank accounts (excluding cash accounts)
 * - Determining if user can add more bank accounts
 *
 * @example
 * ```tsx
 * const { canAddBankAccount, currentBankAccountCount } = useBankAccountLimit();
 *
 * if (!canAddBankAccount) {
 *   alert("Limit reached!");
 * }
 * ```
 */
export function useBankAccountLimit(): UseBankAccountLimitResult {
  const cachedTerreiro = useCachedTerreiro();
  const cachedMedium = useCachedMedium();
  const terreiroId = cachedTerreiro?.id;
  const mediumId = cachedMedium?.id;

  // Fetch subscription features
  const { data: subscriptionData, isLoading: isSubscriptionLoading } =
    useEnhancedSubscriptionFeatures({
      terreiroId,
      mediumId,
      enabled: ENABLE_SUBSCRIPTION_LIMITS,
    });

  // Fetch bank accounts
  const { bankAccounts, isBankAccountsLoading } = useFinancialQueries(
    terreiroId,
    mediumId,
  );

  // Count only real bank accounts (exclude cash accounts)
  const currentBankAccountCount = useMemo(() => {
    if (!bankAccounts) return 0;
    return bankAccounts.filter((account: BankAccountWithType) =>
      isBankAccount(account.accountType),
    ).length;
  }, [bankAccounts]);

  // Get max limit from subscription
  const maxBankAccounts = useMemo(() => {
    if (!subscriptionData?.currentPlan?.features) return null;
    return subscriptionData.currentPlan.features.maxBankAccounts as number;
  }, [subscriptionData]);

  // Check if user can add more bank accounts
  const canAddBankAccount = useMemo(() => {
    // If subscription limits are disabled, always allow
    if (!ENABLE_SUBSCRIPTION_LIMITS) {
      console.log(
        "[useBankAccountLimit] Subscription limits disabled - allowing",
      );
      return true;
    }

    console.log("[useBankAccountLimit] Checking limits:", {
      currentCount: currentBankAccountCount,
      maxBankAccounts,
      hasSubscriptionData: !!subscriptionData,
    });

    // Use isFeatureAllowed with current count for proper validation
    const allowed = isFeatureAllowed(
      subscriptionData,
      "maxBankAccounts",
      currentBankAccountCount,
    );

    console.log(
      `[useBankAccountLimit] Final decision: canAddBankAccount=${allowed}`,
    );
    return allowed;
  }, [subscriptionData, currentBankAccountCount, maxBankAccounts]);

  return {
    canAddBankAccount,
    currentBankAccountCount,
    maxBankAccounts,
    isLoading: isSubscriptionLoading || isBankAccountsLoading,
    subscriptionData,
  };
}
